"use client";
import React from "react";
import { Box, Typography, Container, Paper, Chip } from "@mui/material";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

export default function RecruitersSection() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const cardsRaw = t("recruitersSection.cards", { returnObjects: true });
  const cards = Array.isArray(cardsRaw) ? cardsRaw : [];

  const cardImages = [
    IMAGES.COMPANY_IMG_1,
    IMAGES.COMPANY_IMG_2,
    IMAGES.COMPANY_IMG_3,
  ];

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 8, direction: isArabic ? "rtl" : "ltr" }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-start", lg: "flex-start" },
          px: { xs: 2, sm: 2, md: 3, lg: 0 },
        }}
      >
        <Chip
          label={t("recruitersSection.chip")}
          sx={{
            backgroundColor: "white",
            border: "1px solid #0206174D",
            mb: 2,
            color: "#647482",
            fontSize: { xs: "12px", sm: "13px", md: "14px" },
            fontWeight: "400",
            textTransform: "capitalize",
            px: 1,
            py: 1.5,
            fontFamily: "var(--font-plusjakarta)",
            borderRadius: "40px",
          }}
        />
      </Box>

      {/* Title + Subtitle */}
      <Box
        sx={{
          mb: 6,
          px: { xs: 2, sm: 2, md: 3, lg: 0 },
          textAlign: {
            xs: isArabic ? "right" : "left",
            lg: isArabic ? "right" : "left",
          },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 0,
            fontFamily: "var(--font-plusjakarta)",
            color: "#000",
            fontSize: {
              xs: "24px",
              sm: "28px",
              md: "36px",
              lg: "36px",
              xl: "42px",
            },
            maxWidth: 800,
            lineHeight: { xs: 1.3, sm: 1.3, md: 1.3, lg: 1.2 },
          }}
        >
          <span style={{ color: "#775CE5" }}>10,000+</span>{" "}
          {t("recruitersSection.titlePart1").replace("10,000+", "").trim()}
        </Typography>
        <Typography
          sx={{
            fontWeight: "bold",
            mb: 0,
            fontFamily: "var(--font-plusjakarta)",
            color: "#000",
            fontSize: { xs: "24px", sm: "28px", md: "36px", lg: "42px" },
            lineHeight: { xs: 1.3, sm: 1.3, md: 1.3, lg: 1.2 },
          }}
        >
          {t("recruitersSection.titlePart2")}
          <span
            style={{
              color: "#775CE5",
              marginLeft: isArabic ? "0" : "1%",
              marginRight: isArabic ? "1%" : "0",
            }}
          >
            {t("recruitersSection.titleHighlight")}
          </span>
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            color: "#2A2F4699",
            fontWeight: "400",
            fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem", lg: "1.2rem" },
            mt: "1%",
            lineHeight: { xs: 1.5, sm: 1.5, md: 1.5, lg: 1.6 },
          }}
        >
          {t("recruitersSection.subtitle")}
        </Typography>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "column",
            lg: "row",
          },
          flexWrap: { lg: "nowrap" },
          justifyContent: { lg: "space-between" },
          columnGap: { lg: "24px" },
          rowGap: { xs: 3, sm: 3, md: 4, lg: 0 },
          px: { xs: 2, sm: 2, md: 3, lg: 0 },
        }}
      >
        {cards.map((item, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              flex: { lg: "1 1 33%" },
              maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "33.33%" },
              width: { xs: "100%", sm: "100%", md: "100%", lg: "auto" },
              background: "#F8F3FF",
              borderRadius: 4,
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: { xs: 350, sm: 380, md: 400, lg: 420 },
              p: 4,
              pb: 0,
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ textAlign: isArabic ? "right" : "left" }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "var(--font-outfit)",
                  mb: 0.5,
                  color: "#9048FB",
                  fontWeight: "700",
                  fontSize: { xs: "20px", sm: "22px", md: "24px", lg: "25px" },
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 400,
                  color: "#24303A",
                  fontFamily: "var(--font-outfit)",
                  fontSize: {
                    xs: "1rem",
                    sm: "1.1rem",
                    md: "1.15rem",
                    lg: "1.2rem",
                  },
                }}
              >
                {item.desc}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                height: { xs: 180, sm: 200, md: 220, lg: 230 },
                position: "relative",
                mt: "auto",
              }}
            >
              <Image
                src={cardImages[index]}
                alt={item.title}
                fill
                style={{ objectFit: "contain", borderRadius: "12px 12px 0 0" }}
              />
            </Box>
          </Paper>
        ))}
      </Box>
    </Container>
  );
}
